<template>
  <section class="cfo-panel section-card" :class="scope.clsPrefix" v-loading="loading">
    <div class="ew-header">
      <div class="ew-title">E2E 损益结构穿透</div>
    </div>
    <div class="ew-meta">
        <div class="ew-meta-title">损益结构 Sankey · 当月</div>
        <div class="ew-meta-sub">收入来源 · 成本去向 · 利润构成 · 数据来自接口</div>
    </div>
    <div v-if="loading" class="loading-state">数据加载中...</div>
    <div v-else-if="error" class="error-state">数据加载失败，请稍后重试</div>
    <div v-else ref="chartWrap" class="sankey-chart-wrap">
      <div :id="chartId" class="sankey-chart"></div>
    </div>
  </section>
</template>

<script>
import * as echarts from 'echarts'
import { fetchSankey, unwrapDashboardResponse } from '@/api/dashboard'

/**
 * 节点分类：根据节点ID判断所属的端（收入/成本/利润）
 */
function categorizeNode(id) {
  if (id.startsWith('rev')) return 'income'
  if (['cost', 'cost_mat', 'cost_mfg', 'opex'].includes(id)) return 'cost'
  return 'profit'
}

/**
 * 节点端 → 颜色映射
 */
const END_COLORS = {
  income: '#4A5FF3',
  cost: '#E54B3D',
  profit: '#0AA66A'
}

export default {
  name: 'CfoProfitLossSankey',
  data() {
    return {
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-cfo-pl-sankey'
      },
      chartId: 'cfo_sankey_chart_' + this._uid,
      chartInstance: null,
      chartData: null,
      resizeObserver: null,
      resizeTimer: null,
      loading: false,
      error: false
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    this.teardownResizeObserver()
    window.removeEventListener('resize', this.handleResize)
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },
  methods: {
    /**
     * 加载桑基图数据
     */
    async loadData() {
      this.loading = true
      this.error = false
      try {
        const res = await fetchSankey()
        const data = unwrapDashboardResponse(res)
        if (!data || !Array.isArray(data.nodes) || !Array.isArray(data.links)) {
          throw new Error('桑基图数据格式异常')
        }
        this.chartData = data
        this.loading = false
        await this.$nextTick()
        this.drawChart(data)
        this.setupResizeObserver()
      } catch (e) {
        console.error('获取桑基图数据失败:', e)
        this.error = true
        this.loading = false
      }
    },
    /**
     * 格式化金额（始终返回正数格式，符号由调用方决定）
     * @param {number} amt 金额（万元）
     * @returns {string} 格式化后的金额字符串
     */
    formatAmount(amt) {
      const absAmt = Math.abs(amt)
      if (absAmt >= 10000) {
        return (absAmt / 10000).toFixed(2) + '亿'
      }
      return absAmt.toFixed(0) + '万'
    },
    /**
     * 根据容器宽度计算图表响应式参数
     * 右侧预留足够空间，避免末级节点标签（如经营利润）被裁切
     * @param {number} width 容器宽度
     * @returns {{ height: number, fontSize: number, nodeGap: number, nodeWidth: number, left: number, right: number }}
     */
    getResponsiveConfig(width) {
      if (width < 720) {
        return { height: 220, fontSize: 9, nodeGap: 10, nodeWidth: 14, left: 4, right: 88 }
      }
      if (width < 1100) {
        return { height: 240, fontSize: 10, nodeGap: 12, nodeWidth: 16, left: 6, right: 108 }
      }
      return { height: 280, fontSize: 11, nodeGap: 14, nodeWidth: 18, left: 8, right: 120 }
    },
    /**
     * 渲染桑基图
     * @param {Object} data API返回的桑基图数据
     */
    drawChart(data) {
      const dom = document.getElementById(this.chartId)
      if (!dom) return

      const wrap = this.$refs.chartWrap
      const width = (wrap && wrap.clientWidth) || dom.clientWidth || 1200
      const responsive = this.getResponsiveConfig(width)

      if (wrap) {
        wrap.style.height = responsive.height + 'px'
      }
      dom.style.height = responsive.height + 'px'

      // 构建节点映射：id → 含金额的显示名
      const nodeMap = {}
      ;(data.nodes || []).forEach(n => {
        const sign = n.amount < 0 ? '-' : ''
        const formatted = this.formatAmount(n.amount)
        nodeMap[n.id] = n.label + ' ' + sign + formatted
      })

      // 构建ECharts节点数据
      const chartNodes = (data.nodes || []).map(n => {
        const end = categorizeNode(n.id)
        return {
          name: nodeMap[n.id],
          itemStyle: { color: END_COLORS[end] }
        }
      })

      // 构建ECharts连线（所有 value 取绝对值，避免桑基图报错）
      const chartLinks = (data.links || []).map(l => ({
        source: nodeMap[l.source],
        target: nodeMap[l.target],
        value: Math.abs(l.value)
      }))

      if (this.chartInstance) {
        this.chartInstance.dispose()
      }
      this.chartInstance = echarts.init(dom)
      this.chartInstance.setOption({
        tooltip: {
          trigger: 'item',
          triggerOn: 'mousemove',
          confine: true,
          formatter: (params) => {
            if (params.dataType === 'edge') {
              return params.data.source + ' → ' + params.data.target + '<br/>流量：' + this.formatAmount(params.data.value)
            }
            return params.name
          }
        },
        series: [{
          type: 'sankey',
          layout: 'none',
          emphasis: {
            focus: 'adjacency'
          },
          nodeAlign: 'justify',
          nodeGap: responsive.nodeGap,
          nodeWidth: responsive.nodeWidth,
          layoutIterations: 32,
          left: responsive.left,
          right: responsive.right,
          top: 10,
          bottom: 10,
          label: {
            fontSize: responsive.fontSize,
            color: '#64748b',
            position: 'right',
            distance: 6
          },
          lineStyle: {
            color: 'gradient',
            curveness: 0.5,
            opacity: 0.45
          },
          data: chartNodes,
          links: chartLinks
        }]
      }, true)
      this.$nextTick(() => {
        if (this.chartInstance) {
          this.chartInstance.resize()
        }
      })
    },
    /**
     * 监听容器尺寸变化，比仅监听 window 更适配工作台布局伸缩
     */
    setupResizeObserver() {
      this.teardownResizeObserver()
      const wrap = this.$refs.chartWrap
      if (!wrap || typeof ResizeObserver === 'undefined') return
      this.resizeObserver = new ResizeObserver(() => {
        this.handleResize()
      })
      this.resizeObserver.observe(wrap)
    },
    teardownResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
    },
    /**
     * 窗口/容器大小变化时自适应图表尺寸与密度
     */
    handleResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer)
      }
      this.resizeTimer = setTimeout(() => {
        if (!this.chartInstance || !this.chartData) return
        const wrap = this.$refs.chartWrap
        const width = (wrap && wrap.clientWidth) || 0
        if (!width) {
          this.chartInstance.resize()
          return
        }
        const responsive = this.getResponsiveConfig(width)
        if (wrap) {
          wrap.style.height = responsive.height + 'px'
        }
        const dom = document.getElementById(this.chartId)
        if (dom) {
          dom.style.height = responsive.height + 'px'
        }
        this.chartInstance.setOption({
          series: [{
            nodeGap: responsive.nodeGap,
            nodeWidth: responsive.nodeWidth,
            left: responsive.left,
            right: responsive.right,
            label: {
              fontSize: responsive.fontSize,
              color: '#64748b',
              position: 'right',
              distance: 6
            }
          }]
        })
        this.chartInstance.resize()
      }, 100)
    }
  }
}
</script>

<style lang="scss" scoped>
.rvc-cosmoplat-workspace-cfo-pl-sankey {
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  background-color: #fff;
  padding: 16px;
  border-radius: 4px;

  .ew-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 10px;
    min-width: 0;
  }

  .ew-title {
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 600;
    color: #1e2226;
    line-height: 20px;
  }

  .ew-meta {
    text-align: right;
    min-width: 0;
  }

  .ew-meta-title {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    line-height: 1.4;
  }

  .ew-meta-sub {
    margin-top: 2px;
    font-size: 11px;
    color: #94a3b8;
    line-height: 1.4;
  }

  .sankey-chart-wrap {
    width: 100%;
    min-width: 0;
    height: 280px;
    overflow: visible;
  }

  .sankey-chart {
    width: 100%;
    height: 100%;
  }

  .loading-state,
  .error-state {
    padding: 40px;
    text-align: center;
    font-size: 12px;
    color: #94a3b8;
  }

  .error-state {
    color: #dc2626;
  }
}

@media screen and (max-width: 900px) {
  .rvc-cosmoplat-workspace-cfo-pl-sankey {
    .ew-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
    }

    .ew-meta {
      text-align: left;
    }
  }
}
</style>
